import { forwardRef, useEffect, useState } from "react";
import { Drawer, Box, Typography, IconButton } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../SnackBar";
import { useFormik } from "formik";
import * as styles from "./styles/styles";
import DeleteAccountDialog from "../Modals/DeleteAccountDialog/DeleteAccountDialog";
import endpoints from "../../Helpers/Endpoints";
import { apiDeleteRequest, apiPutRequest } from "../../Helpers/Requests";
import { apiGetRequest } from "../../Helpers/Requests";
import profileValidationSchema from "./Schema";
import { useImageUpload } from "../ImageUploader/useImageUpload";
import { useRevalidateOnLanguageChange } from "../../hooks/useRevalidateOnLanguageChange";
import emitter from "../../Utils/events/eventBus";
import ManageProfile from "./components/ManageProfile";
import MyOrders from "./components/MyOrders";
import CustomFilterTabs from "../CustomFilterTabs";

const ProfileDrawer = forwardRef(({ open, onClose, defaultTab }, ref) => {
  const { t } = useTranslation("profile-drawer", "validation-error-messages");
  const snackbar = useSnackbar();
  const [selectedOrderTabs, setSelectedOrderTabs] = useState(["all"]);
  const [openDeleteAccountDialog, setOpenDeleteAccountDialog] = useState(false);
  const [isloading, setIsLoading] = useState(false);
  const [actionLoader, setActionLoader] = useState(false);
  const [profileData, setProfileData] = useState({});
  const [initialImageUrl, setInitialImageUrl] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [tempImageFile, setTempImageFile] = useState(null);
  const [imageDeleted, setImageDeleted] = useState(false);
  const [activeTab, setActiveTab] = useState(defaultTab);

  const getProfileData = async () => {
    try {
      setIsLoading(true);
      const response = await apiGetRequest(endpoints.getProfileData);
      setProfileData(response?.data);
      setInitialImageUrl(response?.data?.imageUrl);
      setImageUrl(response?.data?.imageUrl);
      return response?.data;
    } catch (err) {
      console.log("Profile data error:", err);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const { header, headerTitle, drawerPaper, drawerContainer, closeButton } =
    styles;

  const validationSchema = profileValidationSchema(t);

  // const phoneWithoutPlus = profileData.phone?.startsWith("+")
  //   ? profileData.phone.slice(1)
  //   : profileData.phone || "";

  const { uploadFile } = useImageUpload(
    endpoints.updateProfileImage,
    (url) => {
      setImageUrl(url);
      setInitialImageUrl(url);
      // Tell the sidebar to refresh its avatar — the sidebar uses
      // profiles.image_uid as the primary source, so a profile-picture
      // change should be reflected there immediately (without waiting
      // for a full page reload).
      emitter.emit("profile-image-updated", url);
    },
    (message) => snackbar(message, "error")
  );

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: validationSchema,
    initialValues: {
      imageUrl: profileData.imageUrl || "",
      firstName: profileData.firstName || "",
      lastName: profileData.lastName || "",
      email: profileData.email || "",
      // phone: phoneWithoutPlus,
      phonePrefix: profileData.phonePrefix || "+972",
      phoneNumber: profileData.phoneNumber || "",
      address: profileData.address || "",
      postalCode: profileData.postalCode || "",
      birthday: profileData.birthday || "",
      agreeMarketingMaterials: profileData.agreeMarketingMaterials ?? true,
      agreeShareDetailsWithCM: profileData.agreeShareDetailsWithCM ?? true,
    },
    onSubmit: async (values) => {
      try {
        setIsLoading(true);
        setActionLoader(true);
        if (tempImageFile) {
          await uploadFile(tempImageFile);
        }

        if (imageDeleted && initialImageUrl && !tempImageFile) {
          await apiDeleteRequest(endpoints.updateProfileImage);
          // Profile image removed — sidebar should drop back to the
          // initials fallback (or the supplier-side business image on the
          // next sidebar refresh, since the BE COALESCE has it as a
          // fallback). Emit empty so the local listener clears the URL.
          emitter.emit("profile-image-updated", "");
        }

        const {
          imageUrl,
          postalCode,
          // phone,
          ...rest
        } = values;
        // const phoneString = String(phone);
        const finalValues = {
          ...rest,
          postalCode: String(postalCode),
          // phone: phoneString,
        };
        console.log("Form submitted:", finalValues);

        await apiPutRequest(endpoints.updateProfileData, finalValues);
        snackbar(t("profileUpdatedSuccess"), "success");
        onClose();
        getProfileData();
      } catch (error) {
        console.log("error: ", error);
        snackbar(t("profileUpdateError"), "error");
      } finally {
        setIsLoading(false);
        setActionLoader(false);
      }
    },
  });

  useRevalidateOnLanguageChange(formik);

  useEffect(() => {
    if (open) {
      formik.resetForm();
      getProfileData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    onClose();
    formik.resetForm();
  };

  const tabsArr = [
    {
      label: t("myOrders"),
      value: "orders",
      disabled: true,
      soonBadge: true,
    },
    {
      label: t("my_card"),
      value: "card",
      disabled: true,
      soonBadge: true,
    },
    {
      label: t("communit_me_coins"),
      value: "coins",
      disabled: true,
      soonBadge: true,
    },
    {
      label: t("manage_profile"),
      value: "profile",
    },
  ];

  const handleTabChange = (_, newValue) => setActiveTab(newValue);

  return (
    <>
      <Drawer
        ref={ref}
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: drawerPaper }}
      >
        <Box sx={drawerContainer}>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <Box sx={header}>
              <Typography sx={headerTitle}>{t("my_profile")}</Typography>

              <IconButton
                onClick={handleClose}
                sx={{ display: { xs: "flex", md: "none" } }}
              >
                <CloseIcon sx={closeButton} />
              </IconButton>
            </Box>

            <Box
              sx={{
                px: "24px",
                borderBottom: 1,
                borderColor: "rgba(234, 234, 234, 1)",
              }}
            >
              <CustomFilterTabs
                tabs={tabsArr}
                activeTab={activeTab}
                onChange={handleTabChange}
                tabItemStyles={{
                  px: 0,
                  py: "6px",
                  marginRight: "32px",
                }}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              {activeTab === "profile" && (
                <ManageProfile
                  {...{
                    formik,
                    isloading,
                    snackbar,
                    imageUrl,
                    setImageUrl,
                    tempImageFile,
                    setTempImageFile,
                    initialImageUrl,
                    setImageDeleted,
                    setOpenDeleteAccountDialog,
                    handleClose,
                    actionLoader,
                  }}
                />
              )}

              {activeTab === "orders" && (
                <MyOrders
                  {...{
                    selectedOrderTabs,
                    setSelectedOrderTabs,
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Drawer>

      <DeleteAccountDialog
        openDialog={openDeleteAccountDialog}
        setOpenDialog={setOpenDeleteAccountDialog}
      />
    </>
  );
});

ProfileDrawer.displayName = "ProfileDrawer";

export default ProfileDrawer;
